/**
 * UpgradeSection
 *
 * Upgrade page with a plan-comparison grid: when the backend reports the
 * partner rate as configured, three cards — Basic / Couples (featured,
 * "Recommended") / Premium — mirroring the marketing site's pricing tiers;
 * otherwise the plain two-card Basic vs Premium comparison with zero partner
 * mentions. One price (monthly), one CTA below the grid. The Couples card
 * carries a two-state line — linked users see that the rate applies
 * automatically at checkout; unlinked users get a quiet link to /partner.
 *
 * Architecture:
 * - Follows section pattern (activate/deactivate lifecycle)
 * - Integrates with SubscriptionAPI for pricing and checkout
 * - Trial copy is status-gated, neutral until resolved: the page renders the
 *   neutral variant ("Upgrade to Premium", no trial promises) from public
 *   /config alone, and swaps to trial copy only when an authenticated /status
 *   resolves with `trialAvailable: true`. A false flag, a failed fetch, or a
 *   lapsed user (trial consumed server-side) all keep the neutral variant —
 *   never promise a trial the backend hasn't verified for THIS user
 * - Every purchase goes through Stripe Checkout on the monthly price; the
 *   partner rate is a coupon the backend attaches/removes, never a price the
 *   user picks here — the Couples card names the rate, not a purchasable plan
 */

import { SubscriptionAPI } from '../core/subscriptionApi.js';
import { publishSubscriptionStatus } from '../core/subscriptionStatus.js';
import { sessionManager } from '../core/SessionManager.js';
import { eventBus } from '../core/utils.js';

/**
 * Render a dollar amount without trailing-zero noise: whole dollars stay bare
 * ("$20"), fractional amounts get two decimals ("$12.50").
 * @param {number} amount
 * @returns {string}
 * @private
 */
function formatMoney(amount) {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

/**
 * @class UpgradeSection
 */
export class UpgradeSection {
  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.container - Container element
   * @param {EventBus} eventBus - Event bus for communication
   */
  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;

    // State
    this.isActive = false;
    this.prices = null;
    this.trial = null;
    // /config `partnerRate` block — `{ available, amount }`; null until loaded.
    this.partnerRate = null;
    this.loading = false;
    this.lapsedState = null; // 'CANCELED' | 'UNPAID' | null
    // Side-channel /status payload, kept so the cards can reflect what the
    // user already owns.
    this.status = null;
    // Trial gate — false until an authenticated /status resolves with
    // `trialAvailable: true`; the page renders neutral copy while false.
    this.trialAvailable = false;

    // Bound methods
    this.handleCheckout = this.handleCheckout.bind(this);
    this.handleFaqToggle = this.handleFaqToggle.bind(this);
    this.handleNavigatePartner = this.handleNavigatePartner.bind(this);
  }

  /**
   * Activate the section - called when page becomes visible.
   *
   * Renders the pricing cards as soon as `loadPrices()` resolves — Basic
   * users (the majority who actually need this page) see the cards without
   * waiting on `/status`, in the neutral (no-trial) variant. Status check
   * runs in parallel and only affects the page if it returns an actionable
   * signal:
   *   - subscribed → redirect to /subscription (see `shouldRedirectSubscribed`)
   *   - CANCELED / UNPAID → re-render with reactivation banner
   *   - `trialAvailable: true` → re-render with the trial variant (CTA,
   *     subtitle, note, trial FAQ); false or a failed fetch stays neutral
   *   - 401 → route to login so the user can re-authenticate
   *   - anything else → leave the cards alone
   */
  async activate() {
    this.isActive = true;
    this.loading = false;

    // Primary path: render cards immediately.
    await this.loadPrices();
    if (!this.isActive) return;
    this.render();
    this.attachListeners();

    // Side channel: check subscription status for redirect / banner, and
    // publish it to the shared snapshot. Fetched directly rather than through
    // `fetchSubscriptionStatus` because the failure envelope is load-bearing
    // here (a 401 routes to login) and that helper swallows it.
    // Fire-and-forget — failures don't block the page.
    SubscriptionAPI.getStatus().then((result) => {
      if (!this.isActive) return;

      if (!result?.success) {
        // Session expired → push to login with a return path so the user
        // lands back here after re-auth.
        if (result?.status === 401) {
          window.location.href = '/login?returnTo=/upgrade';
        }
        return;
      }

      const status = result.data;
      // Hand the payload to the shared snapshot so a visit here refreshes the
      // sidebar badge / header button instead of leaving them on a stale plan.
      publishSubscriptionStatus(status);

      if (status?.isSubscribed && this.shouldRedirectSubscribed(status)) {
        eventBus.emit('navigate', { page: 'subscription' });
        return;
      }

      const lapsedNext = (status?.status === 'CANCELED' || status?.status === 'UNPAID')
        ? status.status
        : null;
      // Strict equality: only a verified true unlocks trial copy.
      const trialNext = status?.trialAvailable === true;

      this.status = status || null;

      // A plain Basic user's payload changes nothing on screen — don't rebuild
      // the page underneath them for it (unless it just unlocked the trial).
      const affectsCards = Boolean(status?.isSubscribed);

      if (lapsedNext !== this.lapsedState || trialNext !== this.trialAvailable || affectsCards) {
        this.lapsedState = lapsedNext;
        this.trialAvailable = trialNext;
        this.render();
        this.attachListeners();
      }
    }).catch((err) => {
      console.warn('[UpgradeSection] getStatus side-channel failed:', err?.message || err);
    });

    console.log('[UpgradeSection] Activated');
  }

  /**
   * Deactivate the section - called when page is hidden
   */
  deactivate() {
    this.isActive = false;
    this.removeListeners();
    console.log('[UpgradeSection] Deactivated');
  }

  /**
   * Load subscription prices, trial and partner-rate configuration from API
   * @private
   */
  async loadPrices() {
    try {
      const result = await SubscriptionAPI.getConfig();
      if (result.success && result.data) {
        this.prices = result.data.prices;
        this.trial = result.data.trial;
        this.partnerRate = result.data.partnerRate || null;
      }
    } catch (error) {
      console.error('[UpgradeSection] Failed to load prices:', error);
    }
  }

  /**
   * Whether the backend offers the partner rate. Config presence is the launch
   * flag: with no coupon configured the page falls back to the two-card
   * layout and never mentions it.
   * @returns {boolean}
   */
  isPartnerRateAvailable() {
    return this.partnerRate?.available === true;
  }

  /**
   * Decide whether a subscribed user has anything to do on this page.
   *
   * Premium is the only paid tier, so anyone already entitled (own paid
   * subscription or a complimentary grant) has nothing to buy here and is sent
   * to /subscription.
   *
   * @param {Object} status - /status payload
   * @returns {boolean} True when the user should be sent to /subscription
   */
  shouldRedirectSubscribed(status) {
    return Boolean(status?.isSubscribed);
  }

  /**
   * True when the user pays for their own Premium plan.
   * @returns {boolean}
   */
  isPremiumPayer() {
    return this.status?.plan === 'premium' && this.status?.hasOwnPaidSubscription === true;
  }

  /**
   * Render the complete page content
   * @private
   */
  render() {
    // Trial copy needs BOTH signals: the status-verified per-user flag (this
    // user still has a trial) and the config days (for honest copy). Until
    // /status resolves, `trialAvailable` is false and the page stays neutral.
    const trialDays = this.trial?.days ?? 0;
    const hasFreeTrial = this.trialAvailable === true && this.trial?.enabled && trialDays > 0;

    // Pricing values with fallbacks
    const monthlyAmount = this.prices?.monthly?.amount ?? 20;
    const partnerRateAvailable = this.isPartnerRateAvailable();
    const partnerAmount = this.partnerRate?.amount ?? 15;

    // CTA text — trial length lives in the note below the button, so the
    // label itself stays short and action-oriented.
    const ctaText = hasFreeTrial ? 'Start Your Free Trial' : 'Upgrade to Premium';
    const ctaNote = hasFreeTrial
      ? `No charge for ${trialDays} days. Cancel anytime.`
      : 'Cancel anytime.';

    const headerSubtitle = hasFreeTrial
      ? `Try Premium for free for ${trialDays} days. No commitment. Cancel anytime.`
      : 'Unlock everything in Premium. No commitment. Cancel anytime.';

    // FAQ items — a trial-specific question leads when the trial is enabled,
    // so users land on reassurance before the generic billing/privacy items.
    const faqItems = [];
    if (hasFreeTrial) {
      faqItems.push({
        q: 'What happens during the free trial?',
        a: `You get full Premium access for ${trialDays} days at no charge. We ask for a card up front, but nothing is charged until day ${trialDays + 1}. Cancel any time before then and you pay $0.`
      });
    }
    faqItems.push({
      q: 'Can I cancel anytime?',
      a: 'Yes, you can cancel your subscription at any time from your account settings.'
    });
    faqItems.push({
      q: hasFreeTrial ? 'What happens after my trial ends?' : 'How does billing work?',
      a: (hasFreeTrial
        ? `Premium starts with a ${trialDays}-day free trial; when it ends you'll be charged ${formatMoney(monthlyAmount)} a month, and canceling before then costs you nothing.`
        : `Premium is ${formatMoney(monthlyAmount)}/month, billed monthly. You'll be charged when you subscribe, and the same amount each month after that.`)
        + (partnerRateAvailable
          ? ` When you're linked with your partner and you're both on Premium, the partner rate brings each of your bills down to ${formatMoney(partnerAmount)}/month.`
          : '')
    });
    if (partnerRateAvailable) {
      faqItems.push({
        q: 'How does the partner rate work?',
        a: `Each of you subscribes to Premium on your own account, and you link as partners from inside the app. Once you're linked and both on Premium, you each pay ${formatMoney(partnerAmount)}/month instead of ${formatMoney(monthlyAmount)}, starting with your next bill — there's no code to enter; the partner rate applies automatically. You each keep your own private account: neither of you can see the other's sessions, insights, or activity. Anything shared between you is shared only when you both choose to share it.`
      });
      faqItems.push({
        q: 'What happens if we split up?',
        a: `Either of you can unlink at any time. You each keep your own plan and your own data — nothing moves between accounts. The partner rate ends at your next bill, and your price returns to ${formatMoney(monthlyAmount)}/month from then on. The same happens if your partner's Premium ends while you stay linked.`
      });
    }
    faqItems.push({
      q: 'Is my data private and secure?',
      a: 'Absolutely. We use bank-level encryption and your conversations are never shared. Payments are securely processed by Stripe.'
    });

    const faqHtml = faqItems.map((item, idx) => `
            <div class="faq__item">
              <button class="faq__question" data-faq="${idx}">
                <span>${item.q}</span>
                <span class="faq__icon">+</span>
              </button>
              <div class="faq__answer" data-faq-answer="${idx}">
                <p>${item.a}</p>
              </div>
            </div>`).join('');

    const lapsedBanner = this.lapsedState === 'UNPAID'
      ? `<div class="subscription-banner subscription-banner--warning">
           <strong>Your subscription lapsed due to a payment issue.</strong>
           Resubscribe to restore access.
         </div>`
      : this.lapsedState === 'CANCELED'
        ? `<div class="subscription-banner subscription-banner--warning">
             <strong>Your previous subscription ended.</strong>
             Resubscribe to restore full access to your Premium data.
           </div>`
        : '';

    this.container.innerHTML = `
      <div class="upgrade-page">
        ${lapsedBanner}
        <!-- Header -->
        <div class="upgrade-header">
          <h1 class="upgrade-header__title">Deepen Your Relationship</h1>
          <p class="upgrade-header__subtitle">${headerSubtitle}</p>
        </div>

        <!-- Plan Cards -->
        ${this.renderPlanCards({ monthlyAmount, partnerAmount, partnerRateAvailable })}

        <!-- CTA Button -->
        <div class="upgrade-cta">
          <button class="btn btn--primary upgrade-cta__btn" data-action="checkout">
            <span class="upgrade-cta__text">${ctaText}</span>
            <span class="upgrade-cta__loading" style="display: none;">
              <svg class="spinner" width="20" height="20" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="40" stroke-dashoffset="10"/>
              </svg>
            </span>
          </button>
          <p class="upgrade-cta__note">${ctaNote}</p>
        </div>

        <!-- FAQ Section (shared component — see css/components/faq.css) -->
        <div class="faq">
          <h3 class="faq__title">Common questions</h3>
          <div class="faq__list">${faqHtml}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render the plan-card grid.
   *
   * Three cards — Basic / Couples (featured, "Recommended" flag) / Premium —
   * when the partner rate is configured; the plain two-card Basic vs Premium
   * comparison with zero partner mentions otherwise, so a coupon-less deploy
   * degrades cleanly. The Couples card is presentation only: it names the
   * partner *rate*, not a purchasable plan, so the CURRENT PLAN badge never
   * lands on it and the single CTA below the grid is unchanged. Its state
   * line derives from the boot-time `coupleId` snapshot — a user who links a
   * partner mid-session sees the unlinked line until reload (accepted).
   *
   * @param {Object} params
   * @param {number} params.monthlyAmount - Premium list price per month
   * @param {number} params.partnerAmount - Discounted per-person partner rate
   * @param {boolean} params.partnerRateAvailable - Whether the rate is configured
   * @returns {string}
   * @private
   */
  renderPlanCards({ monthlyAmount, partnerAmount, partnerRateAvailable }) {
    const ownsPremium = this.isPremiumPayer();

    // Premium's feature set; the Couples card extends it with one
    // couples-specific bullet.
    const premiumFeatures = `
              <li><strong>Unlimited</strong> conversations</li>
              <li><strong>Unlimited</strong> insights</li>
              <li><strong>Every</strong> discovery path</li>
              <li><strong>Full</strong> library access</li>
              <li><strong>Highly</strong> personalized advice</li>`;

    const basicCard = `
          <!-- Basic Plan -->
          <div class="upgrade-plan upgrade-plan--starter">
            ${ownsPremium ? '' : '<span class="upgrade-plan__badge">CURRENT PLAN</span>'}
            <div class="upgrade-plan__header">
              <h2 class="upgrade-plan__name">Basic</h2>
            </div>
            <p class="upgrade-plan__tagline">Beginning your journey</p>
            <div class="upgrade-plan__price">
              <span class="upgrade-plan__amount">$0</span>
              <span class="upgrade-plan__period">/month</span>
            </div>
            <ul class="upgrade-plan__features">
              <li>Core relationship insights</li>
              <li>Limited daily conversations</li>
              <li>Limited discovery paths</li>
              <li>Limited access to guides</li>
            </ul>
          </div>`;

    const premiumCard = `
          <!-- Premium Plan -->
          <div class="upgrade-plan upgrade-plan--premium">
            ${ownsPremium ? '<span class="upgrade-plan__badge">CURRENT PLAN</span>' : ''}
            <div class="upgrade-plan__header">
              <h2 class="upgrade-plan__name">Premium</h2>
            </div>
            <p class="upgrade-plan__tagline">The full path to growth</p>
            <div class="upgrade-plan__price">
              <span class="upgrade-plan__amount">${formatMoney(monthlyAmount)}</span>
              <span class="upgrade-plan__period">/month</span>
            </div>
            <ul class="upgrade-plan__features">${premiumFeatures}
            </ul>
          </div>`;

    if (!partnerRateAvailable) {
      return `
        <div class="upgrade-plans upgrade-plans--two">${basicCard}
${premiumCard}
        </div>`;
    }

    // Two-state line: linked users see the rate as something they unlock at
    // checkout; unlinked users get a quiet pointer to /partner.
    const stateLine = window.app?.currentUser?.coupleId
      ? '<p class="upgrade-plan__state-line upgrade-plan__state-line--linked">The partner rate applies automatically at checkout when you\'re both on Premium.</p>'
      : '<p class="upgrade-plan__state-line">Have a partner? <button type="button" class="upgrade-plan__partner-link" data-action="navigate-partner">Link from the Partner page</button> — the rate applies automatically.</p>';

    const couplesCard = `
          <!-- Couples (the partner rate) — featured; a rate, not a plan, so it
               never carries the CURRENT PLAN badge -->
          <div class="upgrade-plan upgrade-plan--couples">
            <span class="upgrade-plan__flag">Recommended</span>
            <div class="upgrade-plan__header">
              <h2 class="upgrade-plan__name">Couples</h2>
            </div>
            <p class="upgrade-plan__tagline">Grow together, priced together</p>
            <div class="upgrade-plan__price">
              <span class="upgrade-plan__amount">${formatMoney(partnerAmount)}</span>
              <span class="upgrade-plan__period">/month</span>
            </div>
            <p class="upgrade-plan__price-note">Per person, when you're both on Premium</p>
            <ul class="upgrade-plan__features">${premiumFeatures}
              <li><strong>Unlocked</strong> couples features</li>
            </ul>
            ${stateLine}
            <p class="upgrade-plan__reassurance">You each keep your own account and billing.</p>
          </div>`;

    return `
        <div class="upgrade-plans upgrade-plans--three">${basicCard}
${couplesCard}
${premiumCard}
        </div>`;
  }

  /**
   * Attach event listeners
   * @private
   */
  attachListeners() {
    // Checkout buttons (multiple on page)
    const checkoutBtns = this.container.querySelectorAll('[data-action="checkout"]');
    checkoutBtns.forEach(btn => {
      btn.addEventListener('click', this.handleCheckout);
    });

    // FAQ toggles
    const faqQuestions = this.container.querySelectorAll('.faq__question');
    faqQuestions.forEach(question => {
      question.addEventListener('click', this.handleFaqToggle);
    });

    // Partner-page link on the Couples card (rendered only when unlinked)
    const partnerLinks = this.container.querySelectorAll('[data-action="navigate-partner"]');
    partnerLinks.forEach(link => {
      link.addEventListener('click', this.handleNavigatePartner);
    });
  }

  /**
   * Remove event listeners
   * @private
   */
  removeListeners() {
    const checkoutBtns = this.container.querySelectorAll('[data-action="checkout"]');
    checkoutBtns.forEach(btn => {
      btn.removeEventListener('click', this.handleCheckout);
    });

    const faqQuestions = this.container.querySelectorAll('.faq__question');
    faqQuestions.forEach(question => {
      question.removeEventListener('click', this.handleFaqToggle);
    });

    const partnerLinks = this.container.querySelectorAll('[data-action="navigate-partner"]');
    partnerLinks.forEach(link => {
      link.removeEventListener('click', this.handleNavigatePartner);
    });
  }

  /**
   * Handle the Couples card's "Link from the Partner page" link. Emits on the
   * module-level bus — the same channel the subscription redirect in
   * `activate()` uses — so the router receives it regardless of what bus was
   * injected into this section.
   * @private
   */
  handleNavigatePartner() {
    eventBus.emit('navigate', { page: 'partner' });
  }

  /**
   * Handle FAQ toggle
   * @param {Event} event
   * @private
   */
  handleFaqToggle(event) {
    const question = event.currentTarget;
    const item = question.closest('.faq__item');
    const icon = question.querySelector('.faq__icon');

    // Toggle open state
    const isOpen = item.classList.toggle('faq__item--open');

    // Update icon
    if (icon) {
      icon.textContent = isOpen ? '−' : '+';
    }
  }

  /**
   * Handle checkout button click — every purchase goes through Stripe
   * Checkout on the monthly Premium price.
   *
   * @param {Event} event
   * @private
   */
  async handleCheckout(event) {
    if (this.loading) return;

    this.loading = true;
    this.updateLoadingState(true);

    try {
      const priceId = this.prices?.monthly?.id;

      // Emit analytics event
      this.eventBus?.emit('upgrade:checkout-started', { priceId });

      // Build success URL
      const successUrl = `${window.location.origin}/subscription/success`;

      // If the user was mid-chat when they hit upgrade, preserve the session
      // across the Stripe roundtrip. Stripe Checkout is a full-page redirect
      // that wipes all in-memory state, so we persist a pointer in
      // sessionStorage (tab-scoped — discarded if the tab closes) for the resume
      // path to pick up. The session stays ACTIVE on its own (no orphan reaper
      // to evade), so no backend pause call is needed. Best-effort; failures
      // must NOT block the upgrade.
      const currentMode = window.app?.modules?.modeOrchestrator?.getCurrentMode?.();
      const currentSessionId = sessionManager?.getCurrentSessionId?.();
      // Never snapshot a private session — it would round-trip back through
      // resumeSession's SESSION_NOT_ACTIVE-equivalent branch (P5 privacyMode
      // marker) anyway, so persisting the pointer only invites a confusing
      // "couldn't resume" toast on return with nothing to actually resume.
      const isPrivacyMode = window.app?.modules?.privacyModeManager?.getIsPrivacyMode?.();
      if (currentMode && currentSessionId && !isPrivacyMode) {
        try {
          sessionStorage.setItem(
            'app:checkoutReturnSession',
            JSON.stringify({
              mode: currentMode,
              sessionId: currentSessionId,
              timestamp: Date.now()
            })
          );
        } catch (storageErr) {
          console.warn(
            '[UpgradeSection] Failed to persist checkout return session:',
            storageErr?.message || storageErr
          );
        }
      }

      // Redirect to Stripe checkout
      await SubscriptionAPI.redirectToCheckout({ priceId, successUrl });

    } catch (error) {
      console.error('[UpgradeSection] Checkout failed:', error);

      // Emit error event for notification system
      this.eventBus?.emit('notification:show', {
        type: 'error',
        message: 'Unable to start checkout. Please try again.'
      });

      this.eventBus?.emit('upgrade:checkout-failed', { error: error.message });
    } finally {
      this.loading = false;
      this.updateLoadingState(false);
    }
  }

  /**
   * Update loading state on checkout button
   * @param {boolean} loading
   * @private
   */
  updateLoadingState(loading) {
    const checkoutBtn = this.container.querySelector('.upgrade-cta__btn');
    if (!checkoutBtn) return;

    const textEl = checkoutBtn.querySelector('.upgrade-cta__text');
    const loadingEl = checkoutBtn.querySelector('.upgrade-cta__loading');

    if (loading) {
      checkoutBtn.disabled = true;
      if (textEl) textEl.style.display = 'none';
      if (loadingEl) loadingEl.style.display = 'inline-flex';
    } else {
      checkoutBtn.disabled = false;
      if (textEl) textEl.style.display = 'inline';
      if (loadingEl) loadingEl.style.display = 'none';
    }
  }
}

export default UpgradeSection;
